import { override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import ItemList from 'flarum/common/utils/ItemList';
import humanTime from 'flarum/common/helpers/humanTime';
import TagsPage from 'ext:flarum/tags/forum/components/TagsPage';
import collectDescendants from './utils/tagTree';
import registerTagAttributes from './utils/tagAttributes';

// Replaces flarum-tags' own /tags page (a grid of "tiles", one per
// category) with a phpBB-style forum index, matching how champis.net
// itself lists its forums: one section per category (primary tag), each
// listing every subforum below it - any depth, not just direct children,
// since this hierarchy sometimes goes 3 levels (e.g. "Rédactions" ->
// "Liens" -> "Ascomycètes") - with a description, topic/post counts and
// the last message. See forum.less for the layout. TagsPage itself (not
// chunked - a plain top-level import in flarum-tags' own extend.ts) still
// does all the loading/title/history work in oninit(); only the rendering
// is swapped out here.
export default function redesignTagsPage() {
  registerTagAttributes();

  // TagsPage's own oninit() already loads with include=children, and
  // that populates the `children` relationship for *every* tag in the
  // (unfiltered, unpaginated) /api/tags response, not just the roots it
  // renders by default - so tag.children() already works at any depth
  // here, no extra request needed for the 3-level case.
  //
  // `tags`/`loading` are declared `private` on TagsPage's own class - real
  // fields at runtime, but only reachable from outside via a cast (same
  // escape hatch as Application.prototype.mount elsewhere in this
  // codebase), since this override's callback isn't itself a TagsPage
  // method.
  override(TagsPage.prototype, 'contentItems', function () {
    const self = this;
    const items = new ItemList();
    if (self.loading) {
      items.add('loading', m(LoadingIndicator, null));
      return items;
    }
    const tags = app.store.all('tags');
    items.add('forumList', m("div", {
      className: "ChampisForumList"
    }, self.tags.map(category => categoryView(tags, category))), 100);
    return items;
  });

  // Same gray Hero title bar as champis-others' BooksPage/LinksPage
  // ("comme sur les pages de livres, avec un fond gris") - reuses core's
  // own .Hero class as-is (Hero.less's `background: var(--hero-bg)`,
  // unset here so it falls back to --control-bg), nothing new to add to
  // forum.less. Has to go through the hero() slot (PageStructure's own
  // full-bleed `.Page-hero`, rendered *outside* `.Page-container`/
  // `.container`), not contentItems() above: putting it there first
  // (the very first attempt) landed it inside the narrow content column
  // instead, at less than the full page width and with an odd gap below
  // TagsPage's own hero() (still the site's WelcomeHero, a real,
  // visible admin-configured notice here - not empty/removable). override()
  // (not extend()) is required too: extend()'s callback can only mutate
  // the existing return value in place (see its own docblock - "will not
  // be effective on methods which return scalar values" - and a single
  // vnode has nowhere to splice a sibling into), so the only way to
  // render this *alongside* WelcomeHero rather than instead of it is to
  // call the original and return both from an array.
  override(TagsPage.prototype, 'hero', function (original) {
    return [original(), m("header", {
      className: "Hero ForumsHero"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "containerNarrow"
    }, m("h1", {
      className: "Hero-title"
    }, m("i", {
      className: "icon fas fa-comments",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-forum.forum.forums_menu.forums_link')))))];
  });
}
function categoryView(tags, category) {
  const descendants = collectDescendants(tags, category, (a, b) => a.champisOrder() - b.champisOrder());
  if (!descendants.length) return null;
  return m("div", {
    className: "ChampisForumList-category",
    key: category.id()
  }, m("div", {
    className: "ChampisForumList-header"
  }, m("span", {
    className: "ChampisForumList-colName"
  }, category.name()), m("span", {
    className: "ChampisForumList-colTopics"
  }, app.translator.trans('champis-forum.forum.forum_list.topics_column')), m("span", {
    className: "ChampisForumList-colPosts"
  }, app.translator.trans('champis-forum.forum.forum_list.posts_column')), m("span", {
    className: "ChampisForumList-colLast"
  }, app.translator.trans('champis-forum.forum.forum_list.last_post_column'))), descendants.map(_ref => {
    let {
      tag,
      depth
    } = _ref;
    return forumRowView(tag, depth);
  }));
}
function forumRowView(forum, depth) {
  const lastPostedDiscussion = forum.lastPostedDiscussion();
  return m("div", {
    className: "ChampisForumList-row",
    key: forum.id()
  }, m("div", {
    className: "ChampisForumList-colName",
    style: {
      paddingLeft: `${(depth - 1) * 20}px`
    }
  }, m(Link, {
    href: app.route.tag(forum),
    className: "ChampisForumList-title"
  }, forum.name()), forum.description() && m("p", {
    className: "ChampisForumList-description"
  }, forum.description())), m("div", {
    className: "ChampisForumList-colTopics"
  }, forum.discussionCount()), m("div", {
    className: "ChampisForumList-colPosts"
  }, forum.postCount()), m("div", {
    className: "ChampisForumList-colLast"
  }, lastPostedDiscussion ? m(Link, {
    href: app.route.discussion(lastPostedDiscussion, lastPostedDiscussion.lastPostNumber()),
    className: "ChampisForumList-lastPost"
  }, m("span", {
    className: "ChampisForumList-lastTitle"
  }, lastPostedDiscussion.title()), humanTime(lastPostedDiscussion.lastPostedAt())) : m("span", {
    className: "ChampisForumList-noPosts"
  }, app.translator.trans('champis-forum.forum.forum_list.no_posts'))));
}